import { Fragment, ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { RouteFactory } from "@app/routePaths";
import { ArticleDataObject } from "@app/types/Cue";
import type { NavigationItem } from "@app/types/Navigation";
import defaultBTMobileLogo from "@assets/logo-bt-mobile-black.svg";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import Container from "@components/Container/Container";
import { useHeaderState } from "@components/Header/helpers";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { type UserInfoType, useUserInfo } from "@hooks/useUserInfo";
import { ArticleSocialSharingHeader } from "@pages/Article/components/ArticleSocialSharing";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { sectionNavigationItems } from "@util/constant";
import { cn } from "@util/helpers";
import cx from "classnames";

import LoginTrigger from "./LoginTrigger/LoginTrigger";
import LogoMasthead from "./LogoMasthead/LogoMasthead";
import { Navigation } from "./Navigation/Navigation";
import SideMenu from "./SideMenu/SideMenu";
import SubscribeTrigger from "./SubscribeTrigger/SubscribeTrigger";
import ToggleSideMenuButton from "./ToggleSideMenuButton/ToggleSideMenuButton";
import UserInfo from "./UserInfo/UserInfo";
import type { NavigationA } from "./types";

import styles from "./Header.module.scss";

export type SocialLinkConfig = {
  /**
   * @description icon image of the social link
   */
  src: string;
  link?: string;
};

export interface MainHeaderProps {
  prefix?: string;
  dispalyBreadcrumb?: boolean;
  displayArticle?: boolean;
  parentCategory?: string;
  childCategory?: string;
  categoryDataMapping?: Record<string, NavigationItem>;
  article?: ArticleDataObject;
  scrollObj?: ScrollObjArray[];
  masthead?: ReactNode;
}

type ScrollObjArray = {
  id: string;
  scrollW: number;
};

export default function Header({
  prefix,
  dispalyBreadcrumb = false,
  parentCategory = "",
  childCategory,
  categoryDataMapping = {},
  article,
  displayArticle,
  scrollObj,
  masthead,
}: MainHeaderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const headerData: NavigationA = [];

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      <HeaderContainer
        prefix={prefix}
        headerData={headerData}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        dispalyBreadcrumb={dispalyBreadcrumb}
        parentCategory={parentCategory}
        childCategory={childCategory}
        categoryDataMapping={categoryDataMapping}
        displayArticle={displayArticle}
        article={article}
        scrollObj={scrollObj}
      />

      {masthead}

      <Modal headerData={headerData} isOpen={isOpen} setIsClose={handleClose} />
    </>
  );
}

export type HeaderProps = {
  /**
   * @description for prefixing custom attributes as component might be used by different scope
   * @example data-testid={`${prefix}-xxx`}
   */
  prefix?: string;
  children?: ReactNode;
  mastheadTitle?: string;
  mastheadCustomElement?: React.ReactElement;
  headerData?: NavigationA;
  isOpen?: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dispalyBreadcrumb?: boolean;
  parentCategory: string;
  childCategory?: string;
  categoryDataMapping: Record<string, NavigationItem>;
  displayArticle?: boolean;
  article?: ArticleDataObject;
  scrollObj?: ScrollObjArray[];
};

function HeaderContainer({
  prefix = "",
  headerData,
  dispalyBreadcrumb = false,
  setIsOpen,
  parentCategory = "",
  childCategory = "",
  displayArticle,
  article,
  scrollObj,
}: HeaderProps) {
  const { userInfo } = useUserInfo();
  // TODO change to OKTA user info
  // check user typing in useOKTAUserInfo.ts
  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);
  const userDisplayName = OKTAUserInfo?.display_name || OKTAUserInfo?.loginid;
  const { isHeaderStuck, headerRef } = useHeaderState();

  const toggleSideMenu = () => {
    setIsOpen(true);
  };

  const parentCategoryData = sectionNavigationItems?.[parentCategory];

  const scrollW =
    article && scrollObj && scrollObj.find((x) => x.id === article.id);

  return (
    <>
      <header
        ref={headerRef}
        data-testid="navigation-bar"
        className={cx(
          styles.componentHeader,
          "sticky left-0 top-[-1px] z-20 w-full flex-none"
        )}
      >
        {parentCategory && sectionNavigationItems[parentCategory] ? (
          <div
            className={cx(
              dispalyBreadcrumb && isHeaderStuck ? "block" : "hidden",
              "absolute top-0 z-10 flex min-h-[55px] w-full items-center border-b border-gray-175 bg-white"
            )}
          >
            <Container rootClassName="flex max-w-[1900px] w-full items-center justify-between lg:px-6 !px-2 gap-4">
              <div
                className={cx(
                  "flex w-full items-center max-[1300px]:max-w-[50%] max-[991px]:w-auto"
                )}
              >
                <ToggleSideMenuButton
                  rootClassName="self-start"
                  onClick={toggleSideMenu}
                />

                <div className={cx("hidden w-auto items-center lg:block")}>
                  {parentCategoryData ? (
                    <p className="m-0 w-auto text-nowrap border-r border-gray-175 px-3 font-poppins text-2xs font-semibold leading-none tracking-tightest text-gray-850">
                      <Link
                        to={`/${parentCategoryData.link}`}
                        rel="noopener noreferer"
                        reloadDocument
                      >
                        {parentCategoryData.label}
                      </Link>
                    </p>
                  ) : null}
                </div>

                <div
                  className={cx("hidden w-full items-center pl-2 lg:block ")}
                >
                  {parentCategory && sectionNavigationItems[parentCategory] ? (
                    <Breadcrumb
                      rootClassName="m-0"
                      parentCategory={parentCategory}
                      childCategory={childCategory}
                      categoryDataMapping={sectionNavigationItems}
                      fromHeader
                    />
                  ) : null}
                </div>
              </div>

              <div className={cx("flex justify-center lg:hidden")}>
                <LogoMasthead
                  rootClassName="mx-auto p-0"
                  imgWidth={220}
                  imgHeight={19}
                />
              </div>

              <div className="flex items-center justify-end ">
                <div>
                  {userDisplayName ? (
                    <UserInfo
                      rootClassName="text-2xs font-poppins font-semibold"
                      userInfo={
                        {
                          ...userInfo,
                          lastname: userDisplayName,
                          loginId: OKTAUserInfo?.loginid,
                          sub: OKTAUserInfo?.reguserstatus,
                        } as UserInfoType
                      }
                    />
                  ) : (
                    <LoginTrigger title={"Login"} />
                  )}
                </div>
                {OKTAUserInfo?.usertype !== OKTAUserTypeEnum.SUBSCRIBER ? (
                  <div className={cx("ml-4 hidden last-of-type:mr-0 lg:block")}>
                    <SubscribeTrigger />
                  </div>
                ) : null}
              </div>
            </Container>
          </div>
        ) : null}

        {article ? (
          <div
            className={cx(
              displayArticle && isHeaderStuck ? "block" : "hidden",
              "absolute top-0 z-10 min-h-[55px] w-full items-center border-b border-gray-175 bg-white"
            )}
          >
            <Container rootClassName="max-w-[1900px] h-full">
              <div className="flex h-full min-h-[55px] w-full items-center gap-2 md:justify-between">
                <div
                  className={cx(
                    "hidden w-full items-center lg:flex lg:w-auto "
                  )}
                >
                  <div className="hidden flex-none lg:block">
                    <ToggleSideMenuButton
                      rootClassName="self-start"
                      onClick={toggleSideMenu}
                    />
                  </div>
                  <div
                    className={cx(
                      "hidden w-full items-center gap-1 lg:flex lg:pl-2"
                    )}
                  >
                    <div className="h-[1.25rem] flex-none border-r border-gray-175 pr-4">
                      <Link
                        to={`${RouteFactory.home}?ref=logo`}
                        rel="noopener noreferer"
                        reloadDocument
                      >
                        <img
                          src={defaultBTMobileLogo}
                          height={20}
                          width={28}
                          className="h-[1.25rem]"
                          alt="bt logo"
                        />
                      </Link>
                    </div>

                    {parentCategoryData ? (
                      <p className="m-0 w-auto text-nowrap p-3 pr-2 font-poppins text-2xs font-bold leading-none tracking-tightest text-gray-850">
                        <Link
                          to={`${parentCategoryData.link}?ref=header`}
                          rel="noopener noreferer"
                          reloadDocument
                        >
                          {parentCategoryData.label}
                        </Link>
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="flex w-full flex-1 items-center justify-between gap-2 lg:w-auto lg:justify-center lg:gap-4 ">
                  <div className={cx("max-w-[500px]")}>
                    <p className="!mb-[0.563rem] mt-[0.563rem] line-clamp-2 font-lct font-bold leading-tight text-gray-850 lg:!mb-1 lg:mt-1">
                      {article.title}
                    </p>
                  </div>
                  <div>
                    <ArticleSocialSharingHeader
                      articleTitle={article.title}
                      shareUrl={article.shortUrl}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4 ">
                  <div className="hidden items-center lg:flex">
                    <div>
                      {userDisplayName ? (
                        <UserInfo
                          rootClassName="text-2xs font-poppins font-semibold"
                          userInfo={
                            {
                              ...userInfo,
                              lastname: userDisplayName,
                              loginId: OKTAUserInfo?.loginid,
                              sub: OKTAUserInfo?.reguserstatus,
                            } as UserInfoType
                          }
                        />
                      ) : (
                        <LoginTrigger title={"Login"} />
                      )}
                    </div>
                    {OKTAUserInfo?.usertype !== OKTAUserTypeEnum.SUBSCRIBER ? (
                      <div
                        className={cx("ml-4 hidden last-of-type:mr-0 lg:block")}
                      >
                        <SubscribeTrigger />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              <div
                className="absolute left-0 top-full h-[0.125rem] bg-gray-850 transition-all duration-150"
                style={{ width: `${scrollW?.scrollW}%` }}
              ></div>
            </Container>
          </div>
        ) : null}
        {/* bottom */}
        <div
          className={cx(
            "relative flex min-h-[50px] w-full items-center border-b border-gray-175 bg-white"
          )}
        >
          <Container rootClassName="flex min-h-[50px] justify-between lg:grid grid-cols-3 max-w-[1900px] w-full h-full lg:px-6 !px-2">
            <div
              className={cx(
                "flex items-center justify-between  lg:justify-start",
                prefix === "homepage" ? "lg:col-span-2" : "col-auto"
              )}
            >
              <ToggleSideMenuButton onClick={toggleSideMenu} />

              {prefix === "homepage" ? (
                <div className="hidden lg:block">
                  <Navigation data={headerData} position="left" />
                </div>
              ) : null}
            </div>

            <div
              className={cx(
                "items-center justify-center",
                prefix === "homepage" ? "flex lg:hidden" : "flex"
              )}
            >
              <LogoMasthead
                rootClassName="mx-auto"
                imgWidth={220}
                imgHeight={19}
              />
            </div>

            <div className="flex items-center justify-end">
              {prefix === "homepage" ? (
                <div className="hidden lg:block">
                  <Navigation data={headerData} position="right" />
                </div>
              ) : null}
              {prefix === "homepage" ? (
                <div className="mx-2 hidden h-[24px] w-[1px] bg-gray-175 lg:block"></div>
              ) : null}
              <div className={cx("flex items-center justify-center lg:mr-3")}>
                {userDisplayName ? (
                  <UserInfo
                    rootClassName="text-2xs font-poppins font-semibold"
                    userInfo={
                      {
                        ...userInfo,
                        lastname: userDisplayName,
                        loginId: OKTAUserInfo?.loginid,
                        sub: OKTAUserInfo?.reguserstatus,
                      } as UserInfoType
                    }
                  />
                ) : (
                  <LoginTrigger title={"Login"} />
                )}
              </div>
              {OKTAUserInfo?.usertype !== OKTAUserTypeEnum.SUBSCRIBER ? (
                <div className={cx("hidden lg:block")}>
                  <SubscribeTrigger />
                </div>
              ) : null}
            </div>
          </Container>
        </div>
      </header>
      <div className={cx("block pt-3 text-center lg:hidden")}>
        {OKTAUserInfo?.usertype !== OKTAUserTypeEnum.SUBSCRIBER ? (
          <SubscribeTrigger
            displayArrow
            title="Subscribe from S$9.90/month"
            rootClassName={
              "bg-transparent !text-orange-300 visited:!text-orange-300 !font-medium"
            }
          />
        ) : null}
      </div>
    </>
  );
}

export type MastheadProps = {
  prefix?: string;
  mastheadTitle?: string;
  mastheadCustomElement?: React.ReactElement;
  userDisplayName?: boolean;
  mastheadLinks?: NavigationA;
};

export function MastheadContainer({
  mastheadTitle = "",
  mastheadCustomElement,
  mastheadLinks,
}: MastheadProps) {
  return (
    <Container rootClassName="">
      <div className="relative hidden w-full flex-none pb-0 pt-6 lg:block">
        {mastheadTitle && mastheadTitle in sectionNavigationItems ? (
          <>
            <LogoMasthead
              rootClassName="absolute"
              imgWidth={220}
              imgHeight={19}
            />
            {mastheadCustomElement ?? (
              <h2 className="text-center font-playfair text-8xl uppercase">
                <Link
                  to={`/${mastheadTitle}`}
                  rel="noopener noreferer"
                  reloadDocument
                >
                  {sectionNavigationItems?.[mastheadTitle]?.label}
                </Link>
              </h2>
            )}
          </>
        ) : (
          <>
            <div className="text-center uppercase">
              {mastheadLinks?.map((links, index) => {
                const childrenD = links?.section?.children || [];

                return (
                  <div key={`masthead-links-${index}`}>
                    <>
                      {childrenD.map((children, index) => {
                        const { link, label, key } = children;
                        return (
                          <Link
                            key={key}
                            to={`${link}?ref=masthead`}
                            rel="noopener noreferer"
                            reloadDocument
                            className={cn(
                              "border-l border-gray-175 px-[0.375rem] font-poppins text-4xs font-light text-gray-850 hover:underline",
                              { "border-none": index === 0 }
                            )}
                          >
                            {label}
                          </Link>
                        );
                      })}
                    </>
                  </div>
                );
              })}
            </div>
            <LogoMasthead rootClassName={"flex justify-center"} />
          </>
        )}
      </div>
    </Container>
  );
}

export type ModalProps = {
  isOpen: boolean;
  setIsClose: () => void;
  headerData: NavigationA;
};

export const Modal = ({ isOpen, setIsClose, headerData }: ModalProps) => {
  const handleOnClose = () => {
    setIsClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[10000]"
        onClose={handleOnClose}
        aria-label="side-menu-dialog"
        data-testid={"side-menu-dialog"}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0  bg-black/25" />
        </TransitionChild>

        <div
          className="fixed inset-0 overflow-y-auto"
          data-testid={"side-menu-dialog-container"}
        >
          <TransitionChild
            as={Fragment}
            enter="transition ease duration-500 transform"
            enterFrom="opacity-0 -translate-x-12"
            enterTo="opacity-100 translate-x-0"
            leave="transition ease duration-300 transform"
            leaveFrom="opacity-100 translate-x-0"
            leaveTo="opacity-0 -translate-x-12"
          >
            <DialogPanel className="w-full  transform transition-all lg:max-w-[260px]">
              <SideMenu
                data={headerData}
                isSideMenuOpen={isOpen}
                handleOnClose={handleOnClose}
              />
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};
